<template>
  <BottomModal
    v-if="mode === 'bottomSheet'"
    :key="`forgot-bottom-sheet-step-${currentStep}`"
    :visible="true"
    @close="handleCancel"
    :fullHeight="false"
  >
    <template #default>
      <div class="forgot-password-flow p-3">
        <FlowContent />
      </div>
    </template>
  </BottomModal>

  <div v-else class="forgot-password-flow forgot-password-flow--standalone">
    <FlowContent />
  </div>
</template>

<script setup>
import { ref, computed, defineComponent, h, onBeforeUnmount } from 'vue';
import { toast } from 'vue3-toastify';
import api from '@/services/api';
import BottomModal from '@/components/BottomModal.vue';
import { useAuthStore } from '@/stores/authStore';

const props = defineProps({
  mode: {
    type: String,
    default: 'standalone',
    validator: (value) => ['standalone', 'bottomSheet'].includes(value),
  },
  isAuthenticated: {
    type: Boolean,
    default: false,
  },
  onSuccess: {
    type: Function,
    default: () => {},
  },
  onCancel: {
    type: Function,
    default: () => {},
  },
});

const authStore = useAuthStore();

const STEP_CONFIRM_SEND_AUTH = 0;
const STEP_EMAIL = 1;
const STEP_CODE = 2;
const STEP_NEW_PASSWORD = 3;

const currentStep = ref(props.isAuthenticated ? STEP_CONFIRM_SEND_AUTH : STEP_EMAIL);
const email = ref('');
const maskedEmail = ref(maskEmail(authStore.user?.email || ''));
const otp = ref(['', '', '', '', '', '']);
const otpRefs = ref([]);
const otpError = ref('');
const resetToken = ref('');
const resendCountdown = ref(0);

const loadingSend = ref(false);
const loadingVerify = ref(false);
const loadingReset = ref(false);

const newPassword = ref('');
const confirmPassword = ref('');
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const emailError = ref('');
const newPasswordError = ref('');
const confirmPasswordError = ref('');

let resendTimer = null;

const canResend = computed(() => resendCountdown.value <= 0 && !loadingSend.value);
const resendLabel = computed(() => (canResend.value ? 'Reenviar código' : `Reenviar em ${resendCountdown.value}s`));
const isBottomSheet = computed(() => props.mode === 'bottomSheet');
const isEmailStep = computed(() => currentStep.value === STEP_EMAIL);
const isCodeStep = computed(() => currentStep.value === STEP_CODE);
const isPasswordStep = computed(() => currentStep.value === STEP_NEW_PASSWORD);
const isConfirmSendStep = computed(() => currentStep.value === STEP_CONFIRM_SEND_AUTH);

const isPasswordLongEnough = computed(() => newPassword.value.length >= 6);
const passwordsMatch = computed(() => newPassword.value && newPassword.value === confirmPassword.value);
const canResetPassword = computed(() => isPasswordLongEnough.value && passwordsMatch.value && !!resetToken.value);
const primaryActionClass = computed(() => (isBottomSheet.value ? 'btn btn-color-site flex-fill' : 'btn btn-color-site'));
const secondaryActionClass = computed(() => (isBottomSheet.value ? 'btn btn-outline-secondary flex-fill' : 'btn btn-link text-link'));
const actionRowClass = computed(() => (isBottomSheet.value ? 'd-flex gap-3 mt-4' : 'd-grid gap-2'));

function clearResendTimer() {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
}

function startResendCountdown(seconds = 60) {
  clearResendTimer();
  resendCountdown.value = seconds;
  resendTimer = setInterval(() => {
    if (resendCountdown.value > 0) {
      resendCountdown.value -= 1;
      return;
    }
    clearResendTimer();
  }, 1000);
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function validateEmail() {
  emailError.value = '';
  const value = normalizeEmail(email.value);
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    emailError.value = 'Informe seu e-mail.';
    return false;
  }
  if (!regex.test(value)) {
    emailError.value = 'Digite um e-mail válido.';
    return false;
  }
  return true;
}

function maskEmail(rawEmail) {
  const value = normalizeEmail(rawEmail);
  if (!value || !value.includes('@')) return '***';
  const [localPart, domainPart] = value.split('@');
  if (!localPart) return `***@${domainPart || ''}`;

  const keep = Math.min(3, localPart.length);
  const visible = localPart.slice(0, keep);
  const hidden = '*'.repeat(Math.max(4, localPart.length - keep));
  return `${visible}${hidden}@${domainPart || ''}`;
}

function getTargetEmail() {
  if (props.isAuthenticated) {
    return normalizeEmail(authStore.user?.email || '');
  }
  return normalizeEmail(email.value);
}

function handleApiError(error, fallbackMessage) {
  const message = error?.response?.data?.message || fallbackMessage;
  return String(message || fallbackMessage);
}

function getRetryAfterSeconds(error) {
  const rawValue = error?.response?.data?.meta?.retryAfterSeconds;
  const parsedValue = Number(rawValue);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return 60;
  }

  return Math.floor(parsedValue);
}

function parseCodeError(message) {
  const normalized = message.toLowerCase();

  if (normalized.includes('expir')) {
    return 'Código expirado. Solicite um novo.';
  }

  if (normalized.includes('tentativ')) {
    return 'Muitas tentativas. Solicite um novo código.';
  }

  if (normalized.includes('inválido') || normalized.includes('invalido') || normalized.includes('código')) {
    return 'Código inválido. Tente novamente.';
  }

  return 'Código inválido. Tente novamente.';
}

function resetOtp({ keepError = false } = {}) {
  otp.value = ['', '', '', '', '', ''];
  if (!keepError) otpError.value = '';
  requestAnimationFrame(() => {
    otpRefs.value?.[0]?.focus?.();
  });
}

function setOtpRef(element, index) {
  if (!element) return;
  otpRefs.value[index] = element;
}

function handleOtpInput(event, index) {
  const value = String(event.target.value || '').replace(/\D/g, '').slice(0, 1);
  otp.value[index] = value;
  otpError.value = '';

  if (value && index < otp.value.length - 1) {
    otpRefs.value[index + 1]?.focus?.();
  }

  const complete = otp.value.every((digit) => digit.length === 1);
  if (complete && !loadingVerify.value) {
    verifyCode();
  }
}

function handleOtpBackspace(index) {
  if (otp.value[index]) {
    otp.value[index] = '';
    return;
  }

  if (index > 0) {
    otp.value[index - 1] = '';
    otpRefs.value[index - 1]?.focus?.();
  }
}

function handleOtpPaste(event) {
  event.preventDefault();
  const pasted = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
  if (!pasted) return;

  for (let index = 0; index < 6; index += 1) {
    otp.value[index] = pasted[index] || '';
  }

  const nextIndex = Math.min(pasted.length, 5);
  otpRefs.value[nextIndex]?.focus?.();

  if (pasted.length === 6 && !loadingVerify.value) {
    verifyCode();
  }
}

async function sendCode() {
  if (loadingSend.value) return;

  if (!props.isAuthenticated && !validateEmail()) {
    return;
  }

  loadingSend.value = true;
  otpError.value = '';

  try {
    const payload = props.isAuthenticated ? {} : { email: normalizeEmail(email.value) };
    const response = await api.post('/auth/forgot-password/send-code', payload);

    const apiMaskedEmail = response?.data?.maskedEmail;
    const fallbackMasked = maskEmail(getTargetEmail());
    maskedEmail.value = apiMaskedEmail || fallbackMasked;

    toast.success(response?.data?.message || 'Código de redefinição enviado com sucesso.');
    currentStep.value = STEP_CODE;
    resetOtp();
    startResendCountdown(60);
  } catch (error) {
    if (error?.response?.status === 429) {
      startResendCountdown(getRetryAfterSeconds(error));
    }

    const message = handleApiError(error, 'Não foi possível enviar o código. Tente novamente.');
    toast.error(message);
  } finally {
    loadingSend.value = false;
  }
}

async function verifyCode() {
  if (loadingVerify.value) return;

  const code = otp.value.join('');
  if (code.length !== 6) {
    otpError.value = 'Digite o código completo de 6 dígitos.';
    return;
  }

  loadingVerify.value = true;
  otpError.value = '';

  try {
    const payload = props.isAuthenticated
      ? { code }
      : { email: normalizeEmail(email.value), code };

    const response = await api.post('/auth/forgot-password/verify-code', payload);
    resetToken.value = response?.data?.resetToken || '';

    if (!resetToken.value) {
      throw new Error('Reset token ausente na resposta.');
    }

    currentStep.value = STEP_NEW_PASSWORD;
  } catch (error) {
    const message = handleApiError(error, 'Código inválido. Tente novamente.');
    otpError.value = parseCodeError(message);

    if (otpError.value.includes('expirado')) {
      resendCountdown.value = 0;
      clearResendTimer();
    }

    resetOtp({ keepError: true });
  } finally {
    loadingVerify.value = false;
  }
}

async function resendCode() {
  if (!canResend.value) return;
  await sendCode();
}

function validatePasswords() {
  newPasswordError.value = '';
  confirmPasswordError.value = '';

  if (!newPassword.value) {
    newPasswordError.value = 'Informe a nova senha.';
    return false;
  }

  if (newPassword.value.length < 6) {
    newPasswordError.value = 'Mínimo de 6 caracteres';
    return false;
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = 'Confirme a nova senha.';
    return false;
  }

  if (newPassword.value !== confirmPassword.value) {
    confirmPasswordError.value = 'As senhas não coincidem';
    return false;
  }

  return true;
}

async function resetPassword() {
  if (loadingReset.value) return;

  if (!validatePasswords()) {
    return;
  }

  loadingReset.value = true;
  try {
    const payload = {
      resetToken: resetToken.value,
      password: newPassword.value,
      confirmPassword: confirmPassword.value,
    };

    const response = await api.post('/auth/forgot-password/reset-password', payload);
    toast.success(response?.data?.message || 'Senha redefinida com sucesso! 🎉');
    props.onSuccess();
  } catch (error) {
    const message = handleApiError(error, 'Não foi possível completar a ação. Tente novamente.');
    toast.error(message);
  } finally {
    loadingReset.value = false;
  }
}

function goBackFromCode() {
  otpError.value = '';
  if (props.isAuthenticated) {
    currentStep.value = STEP_CONFIRM_SEND_AUTH;
    return;
  }
  currentStep.value = STEP_EMAIL;
}

function handleCancel() {
  clearResendTimer();
  props.onCancel();
}

const FlowContent = defineComponent({
  name: 'ForgotPasswordFlowContent',
  setup() {
    return () => {
      if (isConfirmSendStep.value) {
        return h('div', { class: 'forgot-step text-center' }, [
          h('div', { class: 'forgot-step__icon-wrap', 'aria-hidden': 'true' },
            h('i', { class: 'fas fa-lock forgot-step__icon-fa' })
          ),
          h('h5', { class: 'forgot-step__title mb-1 text-primary-emphasis' }, 'Redefinir senha'),
          h('p', { class: 'forgot-step__subtitle text-muted mb-2' }, 'Enviaremos um código de verificação para:'),
          h('p', { class: 'forgot-step__masked-email mb-4' }, maskedEmail.value || maskEmail(getTargetEmail())),
          h('div', { class: actionRowClass.value }, isBottomSheet.value
            ? [
              h('button', {
                type: 'button',
                class: secondaryActionClass.value,
                onClick: handleCancel,
              }, 'Cancelar'),
              h('button', {
                type: 'button',
                class: primaryActionClass.value,
                disabled: loadingSend.value,
                onClick: sendCode,
              }, loadingSend.value ? 'Enviando...' : 'Enviar código'),
            ]
            : [
              h('button', {
                type: 'button',
                class: primaryActionClass.value,
                disabled: loadingSend.value,
                onClick: sendCode,
              }, loadingSend.value ? 'Enviando...' : 'Enviar código'),
              h('button', {
                type: 'button',
                class: secondaryActionClass.value,
                onClick: handleCancel,
              }, 'Cancelar'),
            ]),
        ]);
      }

      if (isEmailStep.value) {
        return h('div', { class: 'forgot-step text-center' }, [
          h('div', { class: 'forgot-step__icon-wrap', 'aria-hidden': 'true' },
            h('i', { class: 'fas fa-lock forgot-step__icon-fa' })
          ),
          h('h5', { class: 'forgot-step__title mb-1 text-primary-emphasis' }, 'Esqueceu sua senha?'),
          h('p', { class: 'forgot-step__subtitle text-muted mb-4' }, 'Digite seu e-mail e enviaremos um código de verificação.'),
          h('div', { class: 'mb-3 text-start' }, [
            h('label', { class: 'field-label' }, 'E-mail'),
            h('input', {
              class: 'form-control register-input',
              type: 'email',
              inputmode: 'email',
              placeholder: 'nome@email.com',
              value: email.value,
              onInput: (event) => {
                email.value = event.target.value;
                validateEmail();
              },
            }),
            emailError.value ? h('div', { class: 'field-error' }, emailError.value) : null,
          ]),
          h('div', { class: actionRowClass.value }, isBottomSheet.value
            ? [
              h('button', {
                type: 'button',
                class: secondaryActionClass.value,
                onClick: handleCancel,
              }, 'Lembrei minha senha'),
              h('button', {
                type: 'button',
                class: primaryActionClass.value,
                disabled: loadingSend.value,
                onClick: sendCode,
              }, loadingSend.value ? 'Enviando...' : 'Enviar código'),
            ]
            : [
              h('button', {
                type: 'button',
                class: primaryActionClass.value,
                disabled: loadingSend.value,
                onClick: sendCode,
              }, loadingSend.value ? 'Enviando...' : 'Enviar código'),
              h('button', {
                type: 'button',
                class: secondaryActionClass.value,
                onClick: handleCancel,
              }, 'Lembrei minha senha'),
            ]),
        ]);
      }

      if (isCodeStep.value) {
        return h('div', { class: 'forgot-step text-center' }, [
          h('div', { class: 'forgot-step__icon-wrap', 'aria-hidden': 'true' },
            h('i', { class: 'fas fa-envelope forgot-step__icon-fa' })
          ),
          h('h5', { class: 'forgot-step__title mb-2 text-primary-emphasis' }, 'Verifique seu e-mail'),
          h('p', { class: 'forgot-step__subtitle text-muted mb-1' }, 'Enviamos um código de 6 dígitos para'),
          h('p', { class: 'forgot-step__masked-email mb-3' }, maskedEmail.value),
          h('div', { class: 'otp-wrapper mb-2', onPaste: handleOtpPaste }, otp.value.map((digit, index) => h('input', {
            key: `otp-${index}`,
            class: 'form-control otp-input',
            value: digit,
            maxlength: 1,
            inputmode: 'numeric',
            type: 'tel',
            onInput: (event) => handleOtpInput(event, index),
            onKeydown: (event) => {
              if (event.key === 'Backspace') {
                event.preventDefault();
                handleOtpBackspace(index);
              }
            },
            ref: (element) => setOtpRef(element, index),
          }))),
          otpError.value ? h('div', { class: 'field-error mb-2' }, otpError.value) : null,
          isBottomSheet.value
            ? h('div', { class: actionRowClass.value }, [
              h('button', {
                type: 'button',
                class: secondaryActionClass.value,
                onClick: goBackFromCode,
              }, 'Voltar'),
              h('button', {
                type: 'button',
                class: primaryActionClass.value,
                disabled: loadingVerify.value,
                onClick: verifyCode,
              }, loadingVerify.value ? 'Verificando...' : 'Verificar código'),
            ])
            : h('div', { class: 'd-grid gap-2 mb-2' }, [
              h('button', {
                type: 'button',
                class: 'btn btn-color-site',
                disabled: loadingVerify.value,
                onClick: verifyCode,
              }, loadingVerify.value ? 'Verificando...' : 'Verificar código'),
            ]),
          h('div', { class: isBottomSheet.value ? 'd-flex justify-content-center mt-2' : 'd-flex justify-content-between align-items-center mt-2' }, [
            !isBottomSheet.value
              ? h('button', {
                type: 'button',
                class: 'btn btn-link text-link p-0',
                onClick: goBackFromCode,
              }, 'Voltar')
              : null,
            h('button', {
              type: 'button',
              class: 'btn btn-link text-link p-0',
              disabled: !canResend.value,
              onClick: resendCode,
            }, resendLabel.value),
          ]),
        ]);
      }

      return h('div', { class: 'forgot-step text-center' }, [
        h('div', { class: 'forgot-step__icon-wrap', 'aria-hidden': 'true' },
          h('i', { class: 'fas fa-key forgot-step__icon-fa' })
        ),
        h('h5', { class: 'forgot-step__title mb-2 text-primary-emphasis' }, 'Crie uma nova senha'),
        h('p', { class: 'forgot-step__subtitle text-muted mb-4' }, 'Sua nova senha deve ter pelo menos 6 caracteres.'),

        h('div', { class: 'mb-3 text-start' }, [
          h('label', { class: 'field-label' }, 'Nova senha'),
          h('div', { class: 'password-field-wrapper' }, [
            h('input', {
              class: 'form-control register-input',
              type: showNewPassword.value ? 'text' : 'password',
              placeholder: 'Digite a nova senha',
              value: newPassword.value,
              onInput: (event) => {
                newPassword.value = event.target.value;
                if (newPassword.value.length < 6) {
                  newPasswordError.value = 'Mínimo de 6 caracteres';
                } else {
                  newPasswordError.value = '';
                }
                if (confirmPassword.value && confirmPassword.value !== newPassword.value) {
                  confirmPasswordError.value = 'As senhas não coincidem';
                } else {
                  confirmPasswordError.value = '';
                }
              },
            }),
            h('button', {
              type: 'button',
              class: 'password-toggle-btn',
              tabindex: '-1',
              onClick: () => {
                showNewPassword.value = !showNewPassword.value;
              },
            }, [h('i', { class: showNewPassword.value ? 'fas fa-eye-slash' : 'fas fa-eye' })]),
          ]),
          newPasswordError.value ? h('div', { class: 'field-error' }, newPasswordError.value) : null,
        ]),

        h('div', { class: 'mb-3 text-start' }, [
          h('label', { class: 'field-label' }, 'Confirmar nova senha'),
          h('div', { class: 'password-field-wrapper' }, [
            h('input', {
              class: 'form-control register-input',
              type: showConfirmPassword.value ? 'text' : 'password',
              placeholder: 'Repita a nova senha',
              value: confirmPassword.value,
              onInput: (event) => {
                confirmPassword.value = event.target.value;
                if (confirmPassword.value && confirmPassword.value !== newPassword.value) {
                  confirmPasswordError.value = 'As senhas não coincidem';
                } else {
                  confirmPasswordError.value = '';
                }
              },
            }),
            h('button', {
              type: 'button',
              class: 'password-toggle-btn',
              tabindex: '-1',
              onClick: () => {
                showConfirmPassword.value = !showConfirmPassword.value;
              },
            }, [h('i', { class: showConfirmPassword.value ? 'fas fa-eye-slash' : 'fas fa-eye' })]),
          ]),
          confirmPasswordError.value ? h('div', { class: 'field-error' }, confirmPasswordError.value) : null,
        ]),

        h('div', { class: 'forgot-validations mb-3 text-start' }, [
          h('p', { class: ['forgot-validations__item', isPasswordLongEnough.value ? 'is-ok' : 'is-error'] }, isPasswordLongEnough.value ? '✓ Mínimo de 6 caracteres' : '• Mínimo de 6 caracteres'),
          h('p', { class: ['forgot-validations__item', passwordsMatch.value ? 'is-ok' : 'is-error'] }, passwordsMatch.value ? '✓ Senhas coincidem' : '• Senhas devem coincidir'),
        ]),

        h('div', { class: 'd-grid' }, [
          h('button', {
            type: 'button',
            class: 'btn btn-color-site',
            disabled: !canResetPassword.value || loadingReset.value,
            onClick: resetPassword,
          }, loadingReset.value ? 'Redefinindo...' : 'Redefinir senha'),
        ]),
      ]);
    };
  },
});

onBeforeUnmount(() => {
  clearResendTimer();
});
</script>

<style>
.forgot-password-flow {
  width: 100%;
  padding: 2rem 1rem;
}

.forgot-password-flow--standalone {
  /* flui do topo, igual ao Login */
}

.forgot-step {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 2rem 0;
}

.forgot-step__icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff0f6 0%, #ffe0ef 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.forgot-step__icon-fa {
  font-size: 1.6rem;
  color: var(--primary-color, #f78da7);
}

.forgot-step__icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.forgot-step__title {
  font-size: 1.5rem;
  font-weight: 700;
}

.forgot-step__subtitle {
  font-size: 0.9rem;
  line-height: 1.5;
}

.forgot-step__masked-email {
  color: #ff50a3;
  font-weight: 700;
}

.forgot-password-flow .field-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.forgot-password-flow .field-error {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

.forgot-password-flow .register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.forgot-password-flow .register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
}

.forgot-password-flow .register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

.forgot-password-flow .password-field-wrapper {
  position: relative;
}

.forgot-password-flow .password-field-wrapper .register-input {
  padding-right: 3rem;
}

.forgot-password-flow .password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  z-index: 2;
}

.forgot-password-flow .password-toggle-btn:hover {
  color: #ff50a3;
}

.forgot-password-flow .text-link {
  font-size: 0.9rem;
  color: #ff50a3;
  text-decoration: none;
  display: block;
  text-align: left;
}

.forgot-password-flow .text-link:hover {
  text-decoration: underline;
}

.otp-wrapper {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.otp-input {
  width: 48px;
  height: 52px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  border-radius: 10px;
  border: 1.5px solid #ebebeb;
}

.otp-input:focus {
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
}

.forgot-validations {
  background: #fafafa;
  border: 1px solid #ebebeb;
  border-radius: 10px;
  padding: 10px 12px;
}

.forgot-validations__item {
  margin: 0;
  font-size: 0.82rem;
}

.forgot-validations__item + .forgot-validations__item {
  margin-top: 4px;
}

.forgot-validations__item.is-ok {
  color: #0f766e;
}

.forgot-validations__item.is-error {
  color: #9ca3af;
}

.forgot-password-flow .btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.forgot-password-flow .btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
}

.forgot-password-flow .btn-color-site:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.forgot-password-flow .btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
  font-weight: 600;
}

.forgot-password-flow .btn-outline-secondary:hover {
  background-color: #f8f9fa;
  color: #495057;
  border-color: #adb5bd;
}
</style>
